import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import MilestoneDocumentModal from '@/components/MilestoneDocumentModal';
import TimeLogModal from '@/components/TimeLogModal';
import { Search, FolderOpen, Clock, CheckCircle, AlertCircle, Play, FileText, MessageSquare, Bell } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProjectTracker = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [pendingMessages, setPendingMessages] = useState([]);
  
  // Document Modal State
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [documentType, setDocumentType] = useState(null);
  
  // Time Log Modal State
  const [showTimeLogModal, setShowTimeLogModal] = useState(false);

  useEffect(() => {
    loadProjects();
    loadPendingMessages();
  }, []);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/projects`);
      const projectsData = response.data;
      
      // Fetch variance data for projects with quotes
      const projectsWithVariance = await Promise.all(
        projectsData.map(async (project) => {
          if (project.quote_id) {
            try {
              const varianceRes = await axios.get(`${API}/quotes/${project.quote_id}/variance`);
              return { ...project, variance: varianceRes.data };
            } catch (error) {
              return project;
            }
          }
          return project;
        })
      );
      
      setProjects(projectsWithVariance);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages/pending-actions`);
      setPendingMessages(response.data.all_messages || []);
    } catch (error) {
      console.error('Failed to load pending messages:', error);
    }
  };

  const getProjectMessageCount = (projectId) => {
    return pendingMessages.filter(m => m.project_id === projectId).length;
  };

  const getProjectUrgentCount = (projectId) => {
    return pendingMessages.filter(m => 
      m.project_id === projectId && 
      (m.priority === 'urgent' || m.priority === 'high')
    ).length;
  };

  const openDocumentModal = (project, milestone, docType) => {
    setSelectedProject(project);
    setSelectedMilestone(milestone);
    setDocumentType(docType);
    setShowDocumentModal(true);
  };

  const handleDocumentCreated = (document) => {
    toast.success('Document created successfully');
    loadProjects();
  };

  const filteredProjects = projects.filter((project) => {
    const matchesSearch =
      project.project_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.client_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'All' || project.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      Quoted: { bg: 'bg-gray-500/10', text: 'text-gray-500', border: 'border-gray-500/20', icon: FileText },
      Won: { bg: 'bg-green-500/10', text: 'text-green-500', border: 'border-green-500/20', icon: CheckCircle },
      'In Progress': { bg: 'bg-blue-500/10', text: 'text-blue-500', border: 'border-blue-500/20', icon: Play },
      Completed: { bg: 'bg-purple-500/10', text: 'text-purple-500', border: 'border-purple-500/20', icon: CheckCircle },
      Invoiced: { bg: 'bg-yellow-500/10', text: 'text-yellow-500', border: 'border-yellow-500/20', icon: Clock },
    };

    const config = statusConfig[status] || statusConfig['Quoted'];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-mono uppercase tracking-wide border ${config.bg} ${config.text} ${config.border}`}>
        <Icon className="w-3 h-3" />
        {status}
      </span>
    );
  };

  const stats = {
    total: projects.length,
    inProgress: projects.filter((p) => p.status === 'In Progress').length,
    completed: projects.filter((p) => p.status === 'Completed').length,
    totalValue: projects.reduce((sum, p) => sum + p.quote_total, 0),
  };

  return (
    <div className="p-8" data-testid="project-tracker-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-foreground mb-2" data-testid="project-tracker-title">
          PROJECT TRACKER
        </h1>
        <p className="text-sm font-mono uppercase tracking-wide text-muted-foreground">
          Manage all active and completed projects
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-mono uppercase text-muted-foreground">Total Projects</p>
                <p className="text-2xl font-bold font-mono">{stats.total}</p>
              </div>
              <FolderOpen className="w-8 h-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-mono uppercase text-muted-foreground">In Progress</p>
                <p className="text-2xl font-bold font-mono text-blue-500">{stats.inProgress}</p>
              </div>
              <Play className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-mono uppercase text-muted-foreground">Completed</p>
                <p className="text-2xl font-bold font-mono text-purple-500">{stats.completed}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-mono uppercase text-muted-foreground">Total Value</p>
                <p className="text-xl font-bold font-mono text-green-500">${stats.totalValue.toFixed(2)}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card className="border-border mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  type="text"
                  placeholder="Search by project or client name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 font-mono"
                  data-testid="search-input"
                />
              </div>
            </div>
            <div className="flex gap-2">
              {['All', 'Quoted', 'Won', 'In Progress', 'Completed', 'Invoiced'].map((status) => (
                <Button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  variant={statusFilter === status ? 'default' : 'outline'}
                  size="sm"
                  className="font-mono uppercase text-xs"
                  data-testid={`filter-${status.toLowerCase().replace(' ', '-')}`}
                >
                  {status}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Projects List */}
      {loading ? (
        <Card className="border-border">
          <CardContent className="py-12">
            <p className="text-center text-muted-foreground">Loading projects...</p>
          </CardContent>
        </Card>
      ) : filteredProjects.length === 0 ? (
        <Card className="border-border">
          <CardContent className="py-12">
            <p className="text-center text-muted-foreground">No projects found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredProjects.map((project) => {
            const fileCount = Object.values(project.job_folder || {}).reduce(
              (sum, files) => sum + files.length, 
              0
            );
            const messageCount = getProjectMessageCount(project.id);
            const urgentCount = getProjectUrgentCount(project.id);
            
            return (
              <Card 
                key={project.id} 
                className={`border-border hover:border-primary transition-colors ${
                  urgentCount > 0 ? 'border-red-500/50 shadow-md shadow-red-500/10' : ''
                }`}
                data-testid={`project-${project.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-bold text-lg text-primary">{project.project_name}</span>
                        {getStatusBadge(project.status)}
                        {urgentCount > 0 && (
                          <Badge variant="destructive" className="text-xs font-mono animate-pulse">
                            <Bell size={12} className="mr-1" />
                            {urgentCount} URGENT
                          </Badge>
                        )}
                        {messageCount > 0 && urgentCount === 0 && (
                          <Badge variant="secondary" className="text-xs font-mono">
                            <MessageSquare size={12} className="mr-1" />
                            {messageCount} pending
                          </Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-xs font-mono uppercase text-muted-foreground">Client</p>
                          <p className="font-semibold">{project.client_name}</p>
                        </div>
                        <div>
                          <p className="text-xs font-mono uppercase text-muted-foreground">Quote Total</p>
                          <p className="font-mono font-bold">${project.quote_total.toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-xs font-mono uppercase text-muted-foreground">Job Folder</p>
                          <p className="font-mono">{fileCount} files</p>
                        </div>
                        <div>
                          <p className="text-xs font-mono uppercase text-muted-foreground">Created</p>
                          <p className="font-mono">{new Date(project.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Budget Variance */}
                  {project.variance?.has_project && (
                    <div className="mt-4 p-4 border border-border rounded-sm bg-accent/10">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs font-mono uppercase text-muted-foreground mb-1">Budget Status</p>
                          <Badge 
                            variant={
                              project.variance.summary.status === 'over_budget' ? 'destructive' :
                              project.variance.summary.status === 'under_budget' ? 'default' :
                              'secondary'
                            }
                            className="text-sm font-mono"
                          >
                            {project.variance.summary.status === 'over_budget' ? '📉 OVER BUDGET' :
                             project.variance.summary.status === 'under_budget' ? '📈 UNDER BUDGET' :
                             '✓ ON BUDGET'}
                          </Badge>
                        </div>
                        <div>
                          <p className="text-xs font-mono uppercase text-muted-foreground mb-1">Variance</p>
                          <p className={`text-xl font-bold font-mono ${
                            project.variance.summary.status === 'over_budget' ? 'text-red-500' :
                            project.variance.summary.status === 'under_budget' ? 'text-green-500' :
                            'text-muted-foreground'
                          }`}>
                            {project.variance.summary.total_variance >= 0 ? '+' : ''}
                            ${project.variance.summary.total_variance.toFixed(2)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            ({project.variance.summary.variance_percentage.toFixed(1)}%)
                          </p>
                        </div>
                        <div className="text-sm space-y-1">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Quoted:</span>
                            <span className="font-mono font-semibold">${project.variance.summary.total_quoted.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Actual:</span>
                            <span className="font-mono font-semibold">${project.variance.summary.total_actual.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Milestones */}
                  {project.milestones && project.milestones.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <p className="text-xs font-mono uppercase text-muted-foreground mb-3">Project Milestones</p>
                      <div className="space-y-2">
                        {project.milestones.map((milestone) => (
                          <div 
                            key={milestone.id} 
                            className="flex items-center justify-between p-3 border border-border rounded-sm bg-accent/20"
                          >
                            <div className="flex items-center gap-3 flex-1">
                              <CheckCircle 
                                size={18} 
                                className={milestone.completed ? "text-green-500" : "text-muted-foreground"}
                              />
                              <span className={`text-sm ${milestone.completed ? 'line-through text-muted-foreground' : ''}`}>
                                {milestone.description}
                              </span>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="text-xs font-mono text-muted-foreground">Progress</p>
                                <p className="text-sm font-mono font-bold">
                                  {(milestone.hours_spent || 0).toFixed(1)}h / {milestone.hours_estimated}h
                                </p>
                              </div>
                              <div className="w-24 bg-border rounded-full h-2">
                                <div 
                                  className={`rounded-full h-2 ${milestone.completed ? 'bg-green-500' : 'bg-primary'}`}
                                  style={{ 
                                    width: `${Math.min(((milestone.hours_spent || 0) / milestone.hours_estimated) * 100, 100)}%` 
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Quick Actions */}
                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="text-xs font-mono uppercase text-muted-foreground mb-3">Quick Actions</p>
                    <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                      <Link to={`/projects/${project.id}`}>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="w-full flex-col h-16 gap-1"
                        >
                          <FolderOpen size={16} />
                          <span className="text-xs">Open</span>
                        </Button>
                      </Link>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="flex-col h-16 gap-1"
                        onClick={() => {
                          setSelectedProject(project);
                          setShowTimeLogModal(true);
                        }}
                      >
                        <Clock size={16} />
                        <span className="text-xs">Log Time</span>
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="flex-col h-16 gap-1"
                        onClick={() => navigate(`/projects/${project.id}`)}
                      >
                        <MessageSquare size={16} />
                        <span className="text-xs">Messages</span>
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="flex-col h-16 gap-1"
                        onClick={() => {
                          if (project.milestones && project.milestones.length > 0) {
                            openDocumentModal(project, project.milestones[0], 'qc_sheet');
                          } else {
                            toast.error('No milestones found');
                          }
                        }}
                      >
                        <FileText size={16} />
                        <span className="text-xs">QC Sheets</span>
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="flex-col h-16 gap-1"
                        onClick={() => {
                          if (project.milestones && project.milestones.length > 0) {
                            openDocumentModal(project, project.milestones[0], 'checklist');
                          } else {
                            toast.error('No milestones found');
                          }
                        }}
                      >
                        <CheckCircle size={16} />
                        <span className="text-xs">Checklists</span>
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="flex-col h-16 gap-1"
                        onClick={() => navigate(`/workboard`)}
                      >
                        <Play size={16} />
                        <span className="text-xs">WorkBoard</span>
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="flex-col h-16 gap-1"
                        onClick={() => {
                          if (project.milestones && project.milestones.length > 0) {
                            openDocumentModal(project, project.milestones[0], 'support');
                          } else {
                            toast.error('No milestones found');
                          }
                        }}
                      >
                        <Bell size={16} />
                        <span className="text-xs">Support</span>
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="flex-col h-16 gap-1"
                        onClick={() => {
                          if (project.milestones && project.milestones.length > 0) {
                            openDocumentModal(project, project.milestones[0], 'decision_planning');
                          } else {
                            toast.error('No milestones found');
                          }
                        }}
                      >
                        <AlertCircle size={16} />
                        <span className="text-xs">Decisions</span>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      
      {/* Milestone Document Modal */}
      <MilestoneDocumentModal
        isOpen={showDocumentModal}
        onClose={() => setShowDocumentModal(false)}
        projectId={selectedProject?.id}
        milestone={selectedMilestone}
        documentType={documentType}
        onDocumentCreated={handleDocumentCreated}
      />
      
      {/* Time Log Modal */}
      <TimeLogModal
        isOpen={showTimeLogModal}
        onClose={() => setShowTimeLogModal(false)}
        project={selectedProject}
        onTimeLogged={loadProjects}
      />
    </div>
  );
};

export default ProjectTracker;
